import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { AddIcon, RearrangeIcon, DeleteIcon } from '../../../bombiix-icon';

const style = {
  cursor: 'move',
};

/**
 * Single label-colour option row (drag-reorder + add / remove). Shared by
 * ProductCP, OprationalCP and PIM — mirrors `RenderOptionsItem`'s
 * `enforceOptionRules` gate: PIM passes it to get the `bombiix-icon` glyphs,
 * other modules default to the legacy icon-font spans unchanged.
 */
function RenderLabelOptions({ column, index, moveCard, id, removeLabel, addNewLabel, handleChange, parentIndex, cardEle, wholeArry, enforceOptionRules = false }) {
  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveCard(dragIndex, hoverIndex, cardEle, parentIndex);
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0.5 : 1;
  drag(drop(ref));
  const { t } = useTranslation('common');

  return (
    <div className="mb-2 form-group d-flex w-100" key={id} style={{ ...style, opacity }} data-handler-id={handlerId} ref={ref}>
      <div className="colour-text-container">
        <div className="checkbox-content">
          <div className="checkbox-label no-label margin-auto">
            <input
              onChange={(e) => {
                handleChange(e.target.checked, index, parentIndex, 'isLabelColourChecked');
              }}
              name={`labelColoursOptions${index}`}
              id={`labelColoursOptions${index}`}
              type="checkbox"
              checked={column?.isLabelColourChecked}
              value={column?.isLabelColourChecked}
            />
            <label htmlFor={`labelColoursOptions${index}`} className="chklabel" />
          </div>
        </div>
        <div className="color-content">
          <input
            type="color"
            id="labelColourStyle"
            className="form-control p-0"
            value={column?.colourHexCode}
            onChange={(e) => handleChange(e.target.value, index, parentIndex, 'colourHexCode')}
          />
          <label htmlFor="labelColourStyle" />
        </div>
        <input
          type="text"
          className="text-content f-14"
          value={column?.colourName}
          placeholder={t('PRODUCT_CRITICAL_PATH.MODAL.RENDER_LABEL_OPTIONS.ADD_COLOUR_NAME')}
          onChange={(e) => handleChange(e.target.value, index, parentIndex, 'colourName')}
        />
      </div>
      {wholeArry.length - 1 === index && (
        <button className="btn btn-outline m-0 ml-2" onClick={() => addNewLabel(parentIndex)}>
          {enforceOptionRules ? <AddIcon size={20} /> : <span className="icon-add" />}
        </button>
      )}
      <button className="btn btn-outline m-0 ml-2" id={column?.colourName}>
        {enforceOptionRules ? <RearrangeIcon size={20} /> : <span className="icon-expand" />}
      </button>
      {wholeArry.length > 1 && (
        <button className="btn btn-outline m-0 ml-2" onClick={() => removeLabel(index, parentIndex)}>
          {enforceOptionRules ? <DeleteIcon size={20} /> : <span className="icon-delete" />}
        </button>
      )}
    </div>
  );
}

export default RenderLabelOptions;
